
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Plus, Clock } from 'lucide-react';
import { CustomShoppingItem } from '@/types/shoppingList';
import { cn } from '@/lib/utils';

interface ReactivateItemsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inactiveItems: CustomShoppingItem[];
  onReactivate: (itemId: string) => void;
}

const ReactivateItemsSheet: React.FC<ReactivateItemsSheetProps> = ({
  open,
  onOpenChange,
  inactiveItems,
  onReactivate,
}) => {
  // Group inactive items by category
  const groupedItems = inactiveItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, CustomShoppingItem[]>);

  const sortedCategories = Object.keys(groupedItems).sort();

  const handleReactivate = (itemId: string) => {
    onReactivate(itemId);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Previously Added Items
          </SheetTitle>
          <SheetDescription>
            Quickly re-add items you've added before
          </SheetDescription>
        </SheetHeader>
        
        <div className="overflow-y-auto h-[calc(100%-80px)] pr-2">
          {sortedCategories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No previous items to show.</p>
              <p className="text-sm mt-1">Items you remove will appear here for easy re-adding.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedCategories.map((category) => (
                <div key={category}>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">
                    {category}
                  </h4>
                  <div className="space-y-2">
                    {groupedItems[category].map((item) => (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg",
                          "bg-muted/50 hover:bg-muted transition-colors"
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground">
                            {item.quantity && (
                              <span className="text-primary mr-2">{item.quantity}</span>
                            )}
                            {item.name}
                          </div>
                          {item.note && (
                            <div className="text-sm text-muted-foreground truncate">
                              {item.note}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReactivate(item.id)}
                          className="ml-2 shrink-0"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReactivateItemsSheet;
